"use client"
import { createContext, type ReactNode } from 'react'
import {
  DESKTOP_DIAGRAM_CONFIG,
  type ChooseYourPathDiagramConfig,
  type DiagramArrowSpec,
  type DiagramArrowStyle,
  type DiagramBubble,
} from './chooseYourPathDiagramModel'

// Per-tab designs for the "2029: Choose a Path" diagram + plan-box row.
// Which tab uses which design is CHOOSE_PATH_DIAGRAM_DESIGNS in
// hud/variants.tsx (index-aligned with HUD_VARIANTS); every editable knob
// lives in the literal dicts below.
//
// All non-stock designs share the EDGE-FLUSH layout (Joanna, 2026-07-08):
// the top question spans the full column, "Slow down" sits flush with the
// column's LEFT edge, "Make a deal" flush with its RIGHT edge — instead of
// the stock centered/floating boxes. They also all quiet the question layer
// (by size/weight, not grays) so the five plan boxes read first at a glance.

export type ChoosePathDesignId =
  | 'stock'
  | 'mono-mock'
  | 'no-boxes'
  | 'small-questions'
  | 'red-plan-a'
  | 'serif-mock-layout'
  // Tabs 17–21: refinements of 'small-questions' (Joanna 2026-07-08 — "like
  // this one overall": bigger type, arrows lined up on shared vertical
  // rails, the "Nope" label on the right of its arrow like the others).
  | 'lg-both-rails'
  | 'xl-both-rails'
  | 'lg-right-rail'
  | 'lg-bold-lead'
  | 'bigger-labels'
  // Tabs 27–28: refinements of 'lg-bold-lead' (Joanna 2026-07-08): the
  // "Yes"→C arrow centered between the D and B arrows, ± the question type
  // back at the original 2xl.
  | 'bold-lead-even'
  | 'bold-lead-2xl'
  // Tabs 29–33: 'lg-bold-lead' with QUIETER ARROWS, one de-emphasis
  // vocabulary each (weight / dots / dashes / color / headlessness — color
  // explicitly invited by Joanna for this round).
  | 'quiet-hairline'
  | 'quiet-dotted'
  | 'quiet-dashed'
  | 'quiet-gray'
  | 'quiet-no-heads'
  // Tab 35: tab 27 with the left rail moved 25px toward PLAN D and the
  // "Yes"→C arrow re-centered.
  | 'even-left25'
  // Tabs 36–40: tab 30 fully dotted — heads included — across dot densities
  // (tight → airy) plus one bold-dot weight.
  | 'dots-tight'
  | 'dots-fine'
  | 'dots-loose'
  | 'dots-airy'
  | 'dots-bold'
  // Tab 41: 'dots-tight' with the quote labels in the BODY typeface
  // (serif) italic instead of the inherited face.
  | 'dots-tight-serif-labels'
  // Tab 42: 'quiet-gray' inverted — the BOXES go dark gray, the arrows full black.
  | 'gray-boxes'
  // Tab 43: 'dots-tight' pushed further — more dots (2.5px gap) at a slightly
  // thicker 1.6px dot.
  | 'dots-packed'
  // Tab 44: tab 35 with the path INTO PLAN A at 5px solid black; every other
  // arrow matches the bubbles' 50%-black border color.
  | 'plan-a-bold-path'
  // Tabs 45–46: tab 44's head-consistency options — arrowheads on EVERY line
  // (bold path included) vs. on none.
  | 'plan-a-heads-all'
  | 'plan-a-no-heads'
  // Tab 47: tab 46 with square line ends and the black path SWEEPING in
  // top-to-bottom over a quiet base line.
  | 'plan-a-draw-in'
  // Tab 48: tab 47 with the question + quote type back at the ORIGINAL
  // stock sizes (2xl questions, 18px labels).
  | 'plan-a-draw-in-2xl'
  // Tab 49: tab 48 with the box text back at 22.5px (text-2xl).
  | 'plan-a-draw-in-large'
  // Tab 50: tab 49 with the row-2 boxes widened to fill the row.
  | 'plan-a-full-row2'
  // Tab 51: tab 50 + hover — hovering a plan blackens/thickens its path and
  // its box outline.
  | 'plan-a-hover-paths'

// Which design the current HUD tab wants. Provided once in Playbook.tsx from
// CHOOSE_PATH_DIAGRAM_DESIGNS[hudVersionIndex]; 'stock' everywhere else
// (prerender warmup, tabs 0–6) renders the untouched production design.
export const ChoosePathDesignContext = createContext<ChoosePathDesignId>('stock')

// ---------------------------------------------------------------------------
// Question copy. The mock designs use Joanna's line-broken copy (bold lead
// line, plain continuation); the others keep the production sentences.

const STOCK_RACE_TEXT = (
  <div className="text-black/80">
    <b className="font-bold text-black">Race through the intelligence explosion</b> by
    having AIs self-improve and putting them in charge of more things (datacenters,
    factories, weapons) faster than China can?
  </div>
)
const STOCK_SLOW_DOWN_TEXT = 'Slow down at least a bit to work on safety and governance?'
const STOCK_MAKE_DEAL_TEXT = "Let’s make a deal with China. But what?"

const MOCK_RACE_TEXT = (
  <div>
    <div className="font-bold">Race through the intelligence explosion</div>
    {'by having AIs self-improve and putting them in charge of more things (datacenters, factories, weapons) faster than China can?'}
  </div>
)
const MOCK_SLOW_DOWN_TEXT = (
  <div>
    <div className="font-bold">Slow down at least a bit</div>
    {'for safety and governance?'}
  </div>
)
const MOCK_MAKE_DEAL_TEXT = (
  <div>
    <div className="font-bold">{"Let’s make a deal with China."}</div>
    {'But what?'}
  </div>
)

// ---------------------------------------------------------------------------
// Shared edge-flush geometry. Bubble spans: race 0–100, slow-down 0–48
// (flush left), make-a-deal 62–100 (flush right). Arrow x-attach points are
// chosen so every terminal reaches its plan box with a straight vertical
// (plan boxes are 5 equal flex-1 tracks ≈ 20% each).

const EDGE_FLUSH_ARROWS: DiagramArrowSpec[] = [
  {
    kind: 'link',
    from: { bubbleId: 'race', xPct: 16, edge: 'bottom' },
    to: { bubbleId: 'slow-down', xPct: 16, edge: 'top' },
    label: '“Yes, and that’s good actually\n and/or we have no choice.”',
    labelAnchor: 'left-center',
    labelWidthPct: 42,
  },
  {
    kind: 'link',
    from: { bubbleId: 'race', xPct: 66, edge: 'bottom' },
    to: { bubbleId: 'make-a-deal', xPct: 66, edge: 'top' },
    label: '“What? No, that’s crazy!”',
    labelAnchor: 'left-center',
    labelWidthPct: 32,
  },
  {
    kind: 'terminal',
    from: { bubbleId: 'slow-down', xPct: 10, edge: 'bottom' },
    planIndex: 0,
    label: '“Nope”',
    labelAnchor: 'right-center',
  },
  {
    kind: 'terminal',
    from: { bubbleId: 'slow-down', xPct: 26, edge: 'bottom' },
    planIndex: 1,
    label: '“Yes”',
    labelAnchor: 'left-center',
  },
  {
    kind: 'terminal',
    from: { bubbleId: 'slow-down', xPct: 43, edge: 'bottom' },
    planIndex: 2,
    label: '“Yes, and slow\ndown China too.”',
    labelAnchor: 'left-center',
    labelWidthPct: 22,
  },
  {
    kind: 'terminalBranch',
    from: { bubbleId: 'make-a-deal', xPct: 70, edge: 'bottom' },
    planIndices: [3, 4],
    label: '(Two of many possible deals)',
    labelAnchor: 'left-center',
    labelWidthPct: 34,
  },
]

// EDGE_FLUSH_ARROWS with the arrows snapped onto two shared vertical rails
// (Joanna's line-them-up tweak): the LEFT rail (x16) carries both the
// race→slow-down link and the "Nope" terminal below it; the RIGHT rail (x70)
// carries the race→make-a-deal link and the two-deals stem below it. "Nope"'s
// label sits on the RIGHT of its arrow like every other quote label.
const RAIL_ALIGNED_ARROWS: DiagramArrowSpec[] = [
  {
    kind: 'link',
    from: { bubbleId: 'race', xPct: 16, edge: 'bottom' },
    to: { bubbleId: 'slow-down', xPct: 16, edge: 'top' },
    label: '“Yes, and that’s good actually\n and/or we have no choice.”',
    labelAnchor: 'left-center',
    labelWidthPct: 42,
  },
  {
    kind: 'link',
    from: { bubbleId: 'race', xPct: 70, edge: 'bottom' },
    to: { bubbleId: 'make-a-deal', xPct: 70, edge: 'top' },
    label: '“What? No, that’s crazy!”',
    labelAnchor: 'left-center',
    labelWidthPct: 28,
  },
  {
    kind: 'terminal',
    from: { bubbleId: 'slow-down', xPct: 16, edge: 'bottom' },
    planIndex: 0,
    label: '“Nope”',
    labelAnchor: 'left-center',
  },
  {
    kind: 'terminal',
    from: { bubbleId: 'slow-down', xPct: 26, edge: 'bottom' },
    planIndex: 1,
    label: '“Yes”',
    labelAnchor: 'left-center',
  },
  {
    kind: 'terminal',
    from: { bubbleId: 'slow-down', xPct: 43, edge: 'bottom' },
    planIndex: 2,
    label: '“Yes, and slow\ndown China too.”',
    labelAnchor: 'left-center',
    labelWidthPct: 22,
  },
  {
    kind: 'terminalBranch',
    from: { bubbleId: 'make-a-deal', xPct: 70, edge: 'bottom' },
    planIndices: [3, 4],
    label: '(Two of many possible deals)',
    labelAnchor: 'left-center',
    labelWidthPct: 34,
  },
]

// RAIL_ALIGNED_ARROWS with the "Yes"→C terminal moved to the midpoint of the
// D arrow (x16) and the B arrow (x43), so the three left terminals read as
// evenly spaced. 29.5 also happens to be PLAN C's box center (5 equal tracks
// → center ≈ 30), so the drop lands dead-center in the box.
const RAIL_ALIGNED_EVEN_ARROWS: DiagramArrowSpec[] = RAIL_ALIGNED_ARROWS.map(spec =>
  spec.kind === 'terminal' && spec.planIndex === 1
    ? { ...spec, from: { ...spec.from, xPct: 29.5 } }
    : spec,
)

// RAIL_ALIGNED_EVEN_ARROWS with the LEFT rail (race→slow-down link + "Nope"
// terminal) moved 25px toward PLAN D — 25px ≈ 3.5% of the ~700px text column,
// so x16 → 12.5 — and the "Yes"→C arrow re-centered between the new D arrow
// and the B arrow ((12.5 + 43) / 2 = 27.75).
const RAIL_ALIGNED_EVEN_LEFT25_ARROWS: DiagramArrowSpec[] = RAIL_ALIGNED_EVEN_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'slow-down') {
    return { ...spec, from: { ...spec.from, xPct: 12.5 }, to: { ...spec.to, xPct: 12.5 } }
  }
  if (spec.kind === 'terminal' && spec.planIndex === 0) {
    return { ...spec, from: { ...spec.from, xPct: 12.5 } }
  }
  if (spec.kind === 'terminal' && spec.planIndex === 1) {
    return { ...spec, from: { ...spec.from, xPct: 27.75 } }
  }
  return spec
})

// Tab 35's geometry with the PLAN-A path emphasized (Joanna's 2026-07-09
// mock): the race→make-a-deal link and the two-deals stem run 5px solid
// black with no arrowheads (thick bars, per the mock); the S fan-leg and all
// other arrows drop to the bubbles' border color (50% black). Per-arrow
// styles ride the specs' `stroke` field; unset fields fall back to the
// design's arrowStyle.
const PLAN_A_BOLD_STROKE: DiagramArrowStyle = { strokeWidth: 5, color: '#000000', noHeads: true }
// 2px (was 1.5) so the quiet lines — including the PLAN S fan leg — sit
// crisp on the pixel grid like the design-level arrowStyle.
const PLAN_A_QUIET_STROKE: DiagramArrowStyle = { strokeWidth: 2, color: 'rgba(0, 0, 0, 0.5)' }
// Tab 45's bold stroke: same 5px black but WITH a head, sized up so the V
// reads as an arrowhead rather than a blob at that stroke weight.
const PLAN_A_BOLD_HEAD_STROKE: DiagramArrowStyle = { strokeWidth: 5, color: '#000000', headSize: 14 }
const PLAN_A_HEADS_ALL_ARROWS: DiagramArrowSpec[] = RAIL_ALIGNED_EVEN_LEFT25_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'make-a-deal') {
    return { ...spec, stroke: PLAN_A_BOLD_HEAD_STROKE }
  }
  if (spec.kind === 'terminalBranch') {
    return { ...spec, stroke: PLAN_A_BOLD_HEAD_STROKE, legStrokes: { 4: PLAN_A_QUIET_STROKE } }
  }
  return spec
})

// Tab 47's bold stroke: same 5px black bar, square-ended, drawn in
// top-to-bottom (over the quiet underlay) once the diagram scrolls into
// view. Sweep speed / lead-in delay are the drawIn knobs.
const PLAN_A_DRAW_IN_STROKE: DiagramArrowStyle = {
  strokeWidth: 5,
  color: '#000000',
  noHeads: true,
  squareCaps: true,
  drawIn: { pxPerSecond: 280, initialDelayMs: 400 },
}
const PLAN_A_DRAW_IN_ARROWS: DiagramArrowSpec[] = RAIL_ALIGNED_EVEN_LEFT25_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'make-a-deal') {
    return { ...spec, stroke: PLAN_A_DRAW_IN_STROKE }
  }
  if (spec.kind === 'terminalBranch') {
    return { ...spec, stroke: PLAN_A_DRAW_IN_STROKE, legStrokes: { 4: PLAN_A_QUIET_STROKE } }
  }
  return spec
})

// Tab 48's rail: the bold path scooted 5px LEFT of tab 47's x70 (0.7% of
// the ~700px column) to center the sweep on the PLAN A box (Joanna
// 2026-07-09). Only the black segments move; the S fan-leg re-derives.
const PLAN_A_DRAW_IN_LEFT5_ARROWS: DiagramArrowSpec[] = PLAN_A_DRAW_IN_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'make-a-deal') {
    return { ...spec, from: { ...spec.from, xPct: 69.3 }, to: { ...spec.to, xPct: 69.3 } }
  }
  if (spec.kind === 'terminalBranch') {
    return { ...spec, from: { ...spec.from, xPct: 69.3 } }
  }
  return spec
})

// Tab 51: LEFT5 geometry tagged with which plan's hover lights which arrow —
// the race→slow-down link serves D, C and B; each terminal serves its own
// plan; the S fan-leg emphasizes by its own planIndex automatically. The
// already-black PLAN-A path is left untagged.
const PLAN_A_HOVER_ARROWS: DiagramArrowSpec[] = PLAN_A_DRAW_IN_LEFT5_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'slow-down') {
    return { ...spec, hoverPlanIndices: [0, 1, 2] }
  }
  if (spec.kind === 'terminal') {
    return { ...spec, hoverPlanIndices: [spec.planIndex] }
  }
  return spec
})

// MOBILE variant of the tab-50 arrows: identical geometry and strokes (all
// %-based, so the rails and sweep carry over); labels adjusted for the
// narrow column — widths capped so nothing hangs past the right edge
// (anchor x + width must stay under 100), and the wordy China quote
// shortened like the stock mobile config does, so no label outgrows its
// row gap and collides with the boxes.
const PLAN_A_DRAW_IN_LEFT5_MOBILE_ARROWS: DiagramArrowSpec[] = PLAN_A_DRAW_IN_LEFT5_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'slow-down') {
    // Left rail hugs the edge on mobile (7 vs desktop 12.5): tighter left
    // margin, and "Nope" gains clearance before the C arrow. Label width 58
    // (Joanna 2026-07-09 "wrap a bit less") — 46 broke the quote's first
    // line in two; 58 fits it whole and still clears the right-rail label
    // at x69.3.
    return { ...spec, from: { ...spec.from, xPct: 7 }, to: { ...spec.to, xPct: 7 }, labelWidthPct: 58 }
  }
  if (spec.kind === 'terminal' && spec.planIndex === 0) {
    return { ...spec, from: { ...spec.from, xPct: 7 } }
  }
  if (spec.kind === 'terminal' && spec.planIndex === 1) {
    // Re-centered between the mobile rail (7) and the B arrow (43).
    return { ...spec, from: { ...spec.from, xPct: 25 } }
  }
  if (spec.kind === 'link' && spec.to.bubbleId === 'make-a-deal') {
    return { ...spec, labelWidthPct: 30 }
  }
  if (spec.kind === 'terminal' && spec.planIndex === 2) {
    return { ...spec, label: '“Yes, and slow\nChina too.”', labelWidthPct: 24 }
  }
  if (spec.kind === 'terminalBranch') {
    return { ...spec, labelWidthPct: 30 }
  }
  return spec
})

const PLAN_A_BOLD_PATH_ARROWS: DiagramArrowSpec[] = RAIL_ALIGNED_EVEN_LEFT25_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'make-a-deal') {
    return { ...spec, stroke: PLAN_A_BOLD_STROKE }
  }
  if (spec.kind === 'terminalBranch') {
    // Plan index 4 = PLAN S, the de-emphasized fan leg.
    return { ...spec, stroke: PLAN_A_BOLD_STROKE, legStrokes: { 4: PLAN_A_QUIET_STROKE } }
  }
  return spec
})

// Only the RIGHT rail aligned (link + stem at x70); the left side keeps the
// stock edge-flush x positions ("possibly the left most ones" — this is the
// without-that half of the comparison). "Nope" still labels on the right.
const RIGHT_RAIL_ARROWS: DiagramArrowSpec[] = EDGE_FLUSH_ARROWS.map(spec => {
  if (spec.kind === 'link' && spec.to.bubbleId === 'make-a-deal') {
    return { ...spec, from: { ...spec.from, xPct: 70 }, to: { ...spec.to, xPct: 70 }, labelWidthPct: 28 }
  }
  if (spec.kind === 'terminal' && spec.planIndex === 0) {
    return { ...spec, labelAnchor: 'left-center' as const }
  }
  return spec
})

// ---------------------------------------------------------------------------
// Per-design chrome: bubble box, question type, arrow-label type, arrow
// stroke. Editing a design = editing its entry here.

type ChoosePathChrome = {
  bubbleClassName: string
  questionTextClassName: string
  arrowLabelClassName: string
  arrowStyle: DiagramArrowStyle
  raceText: ReactNode
  slowDownText: ReactNode
  makeDealText: ReactNode
  /** Arrow geometry override; omitted = EDGE_FLUSH_ARROWS. */
  arrows?: DiagramArrowSpec[]
  /** Row-2 bubble widths (% of diagram); omitted = the shared 48 / 38. */
  slowDownWidthPct?: number
  makeDealWidthPct?: number
  /**
   * Exact px gutter between the two row-2 boxes (each box gives up half):
   * pair with 50/50 widths for a fixed center gap that doesn't drift with
   * the column width. Omitted = the widths alone set the gap.
   */
  row2GutterPx?: number
  /** Space above the first row, px; omitted = 0. */
  topPad?: number
  /** Per-row bottom gaps override; omitted = the shared [54, 75, 85]. */
  rowGaps?: number[]
  /**
   * Drop the legacy empty row 0 (an invisible ~110px of estimated height +
   * gap above the race box, inherited from the old three-row stock layout).
   * Rows renumber to 0/1; rowGaps must then have TWO entries.
   */
  compactRows?: boolean
  /** Arrow-tip-to-plan-box gap override; omitted = the shared 5. */
  terminalGap?: number
  /** Line-end-to-box-edge clearance, px; 0 = flush. Omitted = the stock 5. */
  arrowClearancePx?: number
  /** Plan-hover arrow re-stroke; omitted = hover does nothing. */
  hoverEmphasis?: { color: string; strokeWidthDelta: number }
  /** Same-row boxes stretch to matching heights. */
  equalRowHeights?: boolean
}

export const CHOOSE_PATH_DESIGN_CHROME: Record<Exclude<ChoosePathDesignId, 'stock'>, ChoosePathChrome> = {
  // Tab 7 — Joanna's mock, verbatim: mono questions in thin full-black boxes,
  // fine-dotted hairline arrows, mono un-italicized quote labels.
  'mono-mock': {
    bubbleClassName: 'absolute -translate-y-1/2 px-5 py-4 border border-black rounded-[10px]',
    questionTextClassName: 'font-mono text-[15px] leading-relaxed',
    arrowLabelClassName: 'font-mono not-italic text-[12.5px] leading-[17px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, dasharray: '0.1 5', headSize: 8 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
  },
  // Tab 8 — the question layer loses its boxes entirely: bare serif text with
  // thin arrows; only the plan row keeps chrome, so it owns the glance.
  'no-boxes': {
    bubbleClassName: 'absolute -translate-y-1/2 px-0 py-2',
    questionTextClassName: 'text-xl leading-snug text-black/80',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, headSize: 8 },
    raceText: STOCK_RACE_TEXT,
    slowDownText: STOCK_SLOW_DOWN_TEXT,
    makeDealText: STOCK_MAKE_DEAL_TEXT,
  },
  // Tab 9 — the current serif idiom, rebalanced by size alone: questions drop
  // from 2xl to base with 1px borders; the plan chips stay put and now read
  // as the biggest thing in the figure.
  'small-questions': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-base leading-snug',
    arrowLabelClassName: 'text-[14px] italic leading-[15px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: STOCK_RACE_TEXT,
    slowDownText: STOCK_SLOW_DOWN_TEXT,
    makeDealText: STOCK_MAKE_DEAL_TEXT,
  },
  // Tab 10 — hairline map with one red mark: questions as small mono blocks
  // under a top rule (no boxes), dotted hairline arrows, and the recommended
  // plan alone carries red (its border, below).
  'red-plan-a': {
    bubbleClassName: 'absolute -translate-y-1/2 px-0 pt-2 pb-1 border-t border-black',
    questionTextClassName: 'font-mono text-[13px] leading-relaxed',
    arrowLabelClassName: 'font-mono not-italic text-[12px] leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '0.1 5', headSize: 7 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
  },
  // Tab 17 — 'small-questions' with all three of Joanna's tweaks, minimal
  // version: questions up one step (base → lg), labels 14 → 15px, both arrow
  // rails aligned, "Nope" labelled on the right.
  'lg-both-rails': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: STOCK_RACE_TEXT,
    slowDownText: STOCK_SLOW_DOWN_TEXT,
    makeDealText: STOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 18 — same tweaks, type one step further (questions xl, labels 16px):
  // how far readability can go before the questions get loud again.
  'xl-both-rails': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-xl leading-snug',
    arrowLabelClassName: 'text-[16px] italic leading-[17px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: STOCK_RACE_TEXT,
    slowDownText: STOCK_SLOW_DOWN_TEXT,
    makeDealText: STOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 19 — lg type but only the RIGHT rail aligned; the left arrows keep
  // their stock x positions (the "possibly" half of the alignment ask).
  'lg-right-rail': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: STOCK_RACE_TEXT,
    slowDownText: STOCK_SLOW_DOWN_TEXT,
    makeDealText: STOCK_MAKE_DEAL_TEXT,
    arrows: RIGHT_RAIL_ARROWS,
  },
  // Tab 20 — lg type + the mock's bold-lead two-line copy: readability from
  // structure (a scannable bold headline per question) on top of the size bump.
  'lg-bold-lead': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 29 — quieter arrows by WEIGHT: hairline 1px shafts, smaller heads;
  // everything else exactly tab 20.
  'quiet-hairline': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1, headSize: 7 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 30 — quieter arrows by DOTTING: the mock's fine dot chains (solid
  // small heads keep the tips crisp).
  'quiet-dotted': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '0.1 5', headSize: 7 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 31 — quieter arrows by DASHING: short dashes, a step more present
  // than dots but lighter than solid.
  'quiet-dashed': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '5 4', headSize: 7 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 32 — quieter arrows by COLOR alone (Joanna asked for a color
  // variant): tab 20's exact weight/heads at 35% black.
  'quiet-gray': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.35)' },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 33 — quieter arrows by HEADLESSNESS: bare 1.25px lines, no heads at
  // all; top-to-bottom flow carries the direction.
  'quiet-no-heads': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, noHeads: true, headSize: 7 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 35 — tab 27 with the left arrows 25px further left (toward PLAN D's
  // center) and the "Yes"→C arrow re-centered between its neighbors.
  'even-left25': {
    // Bubble text at FULL black (Joanna 2026-07-09) — was the shared text-black/75.
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_EVEN_LEFT25_ARROWS,
  },
  // Tabs 36–40 — tab 30 with EVERYTHING dotted (heads too), stepping the dot
  // gap from tight to airy; 'dots-bold' keeps a mid gap but doubles the dot
  // size instead.
  // Tab 47 — tab 46 with square (butt/miter) line ends everywhere, and the
  // PLAN-A path animating in: all lines start at the quiet gray weight, then
  // the 5px black bar sweeps down the happy path segment by segment.
  'plan-a-draw-in': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.5)', noHeads: true, squareCaps: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: PLAN_A_DRAW_IN_ARROWS,
    // Even line clearances (Joanna 2026-07-09): the bottom row-gap matches
    // the race→row-2 gap, and terminalGap 0 leaves the same 5px line-end
    // clearance above the plan boxes as the links have at bubble tops
    // (paired with the mt-2-less card spec below).
    rowGaps: [54, 75, 75],
    terminalGap: 0,
  },
  // Tab 51 — tab 50 + hover: pointing at a plan re-strokes the lines on its
  // path in full black, 1px thicker (1.5 → 2.5), and blackens that plan
  // box's outline (see this design's card spec below).
  'plan-a-hover-paths': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-2xl leading-snug',
    arrowLabelClassName: 'text-[18px] italic leading-[18px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.5)', noHeads: true, squareCaps: true },
    raceText: (
      <div>
        <b className="font-bold">Race through the intelligence explosion</b>
        {' by having AIs self-improve and putting them in charge of more things (datacenters, factories, weapons) faster than China can?'}
      </div>
    ),
    slowDownText: 'Slow down at least a bit for safety and governance?',
    makeDealText: (
      <div>
        {"Let’s make a deal with China."}
        <br />
        {'But what?'}
      </div>
    ),
    arrows: PLAN_A_HOVER_ARROWS,
    slowDownWidthPct: 49.5,
    makeDealWidthPct: 49.5,
    rowGaps: [54, 75, 75],
    terminalGap: 0,
    hoverEmphasis: { color: '#000000', strokeWidthDelta: 1 },
  },
  // Tab 50 — tab 49 with the second-row boxes stretched to FILL the row:
  // 49.5% each, leaving a 1% center gutter, edges still flush.
  // Box outlines at FULL black (Joanna 2026-07-09) — was border-black/50.
  'plan-a-full-row2': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black rounded-[10px]',
    questionTextClassName: 'text-2xl leading-snug',
    arrowLabelClassName: 'text-[18px] italic leading-[18px] px-2 text-balance',
    // 2px, not 1.5 (Joanna 2026-07-09 "these lines looks fuzzy"): a 1.5px
    // stroke can't put both edges on the pixel grid, so it always anti-
    // aliases; 2px renders crisp.
    arrowStyle: { strokeWidth: 2, headSize: 9, color: 'rgba(0, 0, 0, 0.5)', noHeads: true, squareCaps: true },
    raceText: (
      <div>
        <b className="font-bold">Race through the intelligence explosion</b>
        {' by having AIs self-improve and putting them in charge of more things (datacenters, factories, weapons) faster than China can?'}
      </div>
    ),
    slowDownText: 'Slow down at least a bit for safety and governance?',
    // "But what?" is an unbreakable unit: it drops to its own line only when
    // it genuinely doesn't fit — no orphaned "what?", and no forced break at
    // widths where the sentence flows fine (Joanna 2026-07-09).
    makeDealText: (
      <div>
        {"Let’s make a deal with China. "}
        <span className="whitespace-nowrap">But what?</span>
      </div>
    ),
    arrows: PLAN_A_DRAW_IN_LEFT5_ARROWS,
    // 50/50 with a FIXED 10px center gutter (Joanna 2026-07-09) — was
    // 49.5/49.5, whose 1%-of-column gap read as ~7px.
    slowDownWidthPct: 50,
    makeDealWidthPct: 50,
    row2GutterPx: 10,
    rowGaps: [54, 75, 75],
    terminalGap: 0,
    // Lines flush with the boxes (Joanna 2026-07-09) — was the stock 5px gap.
    arrowClearancePx: 0,
    equalRowHeights: true,
  },
  // Tab 49 — tab 48 verbatim, box text one step up at 22.5px (text-2xl =
  // 1.5rem on the 15px root), the size the boxes had before the body-match.
  'plan-a-draw-in-large': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-2xl leading-snug',
    arrowLabelClassName: 'text-[18px] italic leading-[18px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.5)', noHeads: true, squareCaps: true },
    raceText: (
      <div>
        <b className="font-bold">Race through the intelligence explosion</b>
        {' by having AIs self-improve and putting them in charge of more things (datacenters, factories, weapons) faster than China can?'}
      </div>
    ),
    slowDownText: 'Slow down at least a bit for safety and governance?',
    makeDealText: "Let’s make a deal with China. But what?",
    arrows: PLAN_A_DRAW_IN_LEFT5_ARROWS,
    rowGaps: [54, 75, 75],
    terminalGap: 0,
  },
  // Tab 48 — tab 47 with the type back at the original stock scale: 2xl
  // questions, 18px italic quote labels. Everything else (square caps,
  // sweep-in black path, even line clearances) identical to tab 47.
  'plan-a-draw-in-2xl': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    // text-xl = 1.25rem = exactly the article body's 18.75px (Joanna
    // 2026-07-09: "same size as the body text").
    questionTextClassName: 'text-xl leading-snug',
    arrowLabelClassName: 'text-[18px] italic leading-[18px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.5)', noHeads: true, squareCaps: true },
    // Bold phrase flows INLINE with the sentence (the original production
    // wrap), instead of the mock's headline-on-its-own-line break.
    raceText: (
      <div>
        <b className="font-bold">Race through the intelligence explosion</b>
        {' by having AIs self-improve and putting them in charge of more things (datacenters, factories, weapons) faster than China can?'}
      </div>
    ),
    // Unbolded, flowing (mock wording, bold lead removed — Joanna 2026-07-09).
    slowDownText: 'Slow down at least a bit for safety and governance?',
    makeDealText: "Let’s make a deal with China. But what?",
    arrows: PLAN_A_DRAW_IN_LEFT5_ARROWS,
    rowGaps: [54, 75, 75],
    terminalGap: 0,
  },
  // Tab 45 — tab 44 with arrowheads on EVERY line, the bold path's head
  // enlarged to match its 5px stroke.
  'plan-a-heads-all': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.5)' },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: PLAN_A_HEADS_ALL_ARROWS,
  },
  // Tab 46 — tab 44 with arrowheads on NOTHING: every line is a bare bar;
  // top-to-bottom flow carries the direction.
  'plan-a-no-heads': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.5)', noHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: PLAN_A_BOLD_PATH_ARROWS,
  },
  // Tab 44 — tab 35 with the PLAN-A path emphasized: 5px solid-black bars
  // into make-a-deal and on to PLAN A; all other arrows at the bubble-border
  // gray (50% black) via the design arrowStyle.
  'plan-a-bold-path': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.5)' },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: PLAN_A_BOLD_PATH_ARROWS,
  },
  // Tab 43 — tab 36 denser still: gap 3 -> 2.5 so more dots per run, dot
  // weight 1.25 -> 1.6 so each reads a touch heavier; heads stay dotted.
  'dots-packed': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.6, dasharray: '0.1 2.5', headSize: 9, dottedHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 42 — tab 32 flipped: every BOX outline (question bubbles + plan
  // cards, via the card specs below) drops to dark gray while the arrows
  // return to full black — the paths pop, the containers recede.
  'gray-boxes': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/70 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    // Arrows LIGHT gray (Joanna 2026-07-09), a clear step lighter than the
    // 70%-black box outlines — was full black.
    arrowStyle: { strokeWidth: 1.5, headSize: 9, color: 'rgba(0, 0, 0, 0.3)' },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 41 — tab 36 with the arrow quotes ("Yes", "Nope", ...) set in the
  // body's serif face, italicized, instead of the inherited UI face.
  'dots-tight-serif-labels': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    // Label size dropped 15 -> 13px (Joanna 2026-07-09: "make the italicized ones smaller").
    arrowLabelClassName: 'font-serif text-[13px] italic leading-[14px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '0.1 3', headSize: 9, dottedHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  'dots-tight': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '0.1 3', headSize: 9, dottedHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  'dots-fine': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '0.1 5', headSize: 9, dottedHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  'dots-loose': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '0.1 7', headSize: 10, dottedHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  'dots-airy': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.25, dasharray: '0.1 9', headSize: 11, dottedHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  'dots-bold': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 2, dasharray: '0.1 6', headSize: 10, dottedHeads: true },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 27 — 'lg-bold-lead' with the "Yes"→C arrow centered between the D
  // and B arrows (and dead-center into PLAN C).
  'bold-lead-even': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_EVEN_ARROWS,
  },
  // Tab 28 — same, with the question type back at the ORIGINAL stock size
  // (2xl); everything else (labels, rails, centered C arrow) as tab 27.
  'bold-lead-2xl': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-2xl leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: MOCK_RACE_TEXT,
    slowDownText: MOCK_SLOW_DOWN_TEXT,
    makeDealText: MOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_EVEN_ARROWS,
  },
  // Tab 21 — questions stay at tab 14's base size; only the LABELS grow
  // (14 → 15px, looser leading): tests whether the too-small feeling was the
  // quote labels rather than the questions.
  'bigger-labels': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border border-black/50 rounded-[10px]',
    questionTextClassName: 'text-base leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[17px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.5, headSize: 9 },
    raceText: STOCK_RACE_TEXT,
    slowDownText: STOCK_SLOW_DOWN_TEXT,
    makeDealText: STOCK_MAKE_DEAL_TEXT,
    arrows: RAIL_ALIGNED_ARROWS,
  },
  // Tab 11 — the mock's GEOMETRY without its typography: edge-flush boxes and
  // big-letter plan cards, but in the site's serif/gray idiom with solid
  // arrows — isolates layout from the mono restyle for comparison.
  'serif-mock-layout': {
    bubbleClassName: 'absolute -translate-y-1/2 px-4 py-3 text-black/75 border-2 border-black/50 rounded-[10px]',
    questionTextClassName: 'text-lg leading-snug',
    arrowLabelClassName: 'text-[15px] italic leading-[16px] px-2 text-balance',
    arrowStyle: { strokeWidth: 1.75, headSize: 10 },
    raceText: STOCK_RACE_TEXT,
    slowDownText: STOCK_SLOW_DOWN_TEXT,
    makeDealText: STOCK_MAKE_DEAL_TEXT,
  },
}

const edgeFlushBubbles = (chrome: ChoosePathChrome): DiagramBubble[] => [
  {
    id: 'race',
    row: chrome.compactRows ? 0 : 1,
    xPct: 0,
    anchor: 'left',
    widthPct: 100,
    text: chrome.raceText,
    textClassName: chrome.questionTextClassName,
  },
  {
    id: 'slow-down',
    row: chrome.compactRows ? 1 : 2,
    xPct: 0,
    anchor: 'left',
    widthPct: chrome.slowDownWidthPct ?? 48,
    widthInsetPx: chrome.row2GutterPx ? chrome.row2GutterPx / 2 : undefined,
    text: chrome.slowDownText,
    textClassName: chrome.questionTextClassName,
  },
  {
    id: 'make-a-deal',
    row: chrome.compactRows ? 1 : 2,
    xPct: 100,
    anchor: 'right',
    widthPct: chrome.makeDealWidthPct ?? 38,
    widthInsetPx: chrome.row2GutterPx ? chrome.row2GutterPx / 2 : undefined,
    text: chrome.makeDealText,
    textClassName: chrome.questionTextClassName,
  },
]

const buildEdgeFlushConfig = (chrome: ChoosePathChrome): ChooseYourPathDiagramConfig => ({
  bubbleClassName: chrome.bubbleClassName,
  topPad: chrome.topPad ?? 0,
  rowGaps: chrome.rowGaps ?? [54, 75, 85],
  estimatedRowHeights: chrome.compactRows ? [104, 88] : [56, 104, 88],
  branchStemLength: 18,
  branchCrosslineGap: 18,
  terminalGap: chrome.terminalGap ?? 5,
  arrowClearancePx: chrome.arrowClearancePx,
  hoverEmphasis: chrome.hoverEmphasis,
  equalRowHeights: chrome.equalRowHeights,
  arrowLabelClassName: chrome.arrowLabelClassName,
  arrowStyle: chrome.arrowStyle,
  bubbles: edgeFlushBubbles(chrome),
  arrows: chrome.arrows ?? EDGE_FLUSH_ARROWS,
})

// The redesign's MOBILE config (rendered below lg — see ChooseYourPathOG):
// same language as 'plan-a-full-row2' (edge-flush, square caps, quiet gray
// headless lines, 5px black sweep-in PLAN-A path, full row 2, even
// clearances) at mobile scale: body-ish text, 14px labels, roomier row gaps
// for the taller wrapped labels.
const PLAN_A_FULL_ROW2_MOBILE_CHROME: ChoosePathChrome = {
  // Full-black outline matching the desktop chrome (Joanna 2026-07-09).
  bubbleClassName: 'absolute -translate-y-1/2 px-3 py-2.5 text-black border border-black rounded-[10px]',
  questionTextClassName: 'text-lg leading-snug',
  // 16px — close to the desktop/production 18px (Joanna: mobile labels
  // shouldn't read much smaller than today's).
  arrowLabelClassName: 'text-[16px] italic leading-[17px] px-1 text-balance',
  // 2px like the desktop chrome: both stroke edges on the pixel grid.
  arrowStyle: { strokeWidth: 2, headSize: 9, color: 'rgba(0, 0, 0, 0.5)', noHeads: true, squareCaps: true },
  raceText: (
    <div>
      <b className="font-bold">Race through the intelligence explosion</b>
      {' by having AIs self-improve and putting them in charge of more things (datacenters, factories, weapons) faster than China can?'}
    </div>
  ),
  slowDownText: 'Slow down at least a bit for safety and governance?',
  // Same unbreakable "But what?" unit as desktop: natural flow, and when it
  // wraps it wraps whole.
  makeDealText: (
    <div>
      {"Let’s make a deal with China. "}
      <span className="whitespace-nowrap">But what?</span>
    </div>
  ),
  arrows: PLAN_A_DRAW_IN_LEFT5_MOBILE_ARROWS,
  // 50/50 with the same fixed 10px center gutter as desktop.
  slowDownWidthPct: 50,
  makeDealWidthPct: 50,
  row2GutterPx: 10,
  // No phantom top row, and gaps sized to the tallest wrapped label at
  // 375px (4 lines ≈ 68px + line offsets) — keeps the whole diagram inside
  // one phone screen.
  compactRows: true,
  // The page wrapper's -mt-40 was tuned against the phantom row's ~110px;
  // topPad restores that clearance under the heading explicitly (the gap
  // tightening below keeps the net height saving).
  topPad: 110,
  rowGaps: [95, 85],
  terminalGap: 0,
  // Lines flush with the boxes, matching desktop (Joanna 2026-07-09).
  arrowClearancePx: 0,
  equalRowHeights: true,
}

// Per-design MOBILE configs; designs without an entry fall back to the stock
// MOBILE_DIAGRAM_CONFIG in ChooseYourPathOG.
export const CHOOSE_PATH_MOBILE_CONFIGS: Partial<Record<ChoosePathDesignId, ChooseYourPathDiagramConfig>> = {
  'plan-a-full-row2': buildEdgeFlushConfig(PLAN_A_FULL_ROW2_MOBILE_CHROME),
}

export const CHOOSE_PATH_DESKTOP_CONFIGS: Record<ChoosePathDesignId, ChooseYourPathDiagramConfig> = {
  stock: DESKTOP_DIAGRAM_CONFIG,
  'mono-mock': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['mono-mock']),
  'no-boxes': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['no-boxes']),
  'small-questions': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['small-questions']),
  'red-plan-a': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['red-plan-a']),
  'serif-mock-layout': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['serif-mock-layout']),
  'lg-both-rails': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['lg-both-rails']),
  'xl-both-rails': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['xl-both-rails']),
  'lg-right-rail': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['lg-right-rail']),
  'lg-bold-lead': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['lg-bold-lead']),
  'bigger-labels': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['bigger-labels']),
  'bold-lead-even': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['bold-lead-even']),
  'bold-lead-2xl': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['bold-lead-2xl']),
  'quiet-hairline': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['quiet-hairline']),
  'quiet-dotted': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['quiet-dotted']),
  'quiet-dashed': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['quiet-dashed']),
  'quiet-gray': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['quiet-gray']),
  'quiet-no-heads': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['quiet-no-heads']),
  'even-left25': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['even-left25']),
  'dots-tight': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['dots-tight']),
  'dots-tight-serif-labels': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['dots-tight-serif-labels']),
  'gray-boxes': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['gray-boxes']),
  'dots-packed': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['dots-packed']),
  'plan-a-bold-path': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-bold-path']),
  'plan-a-heads-all': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-heads-all']),
  'plan-a-no-heads': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-no-heads']),
  'plan-a-draw-in': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-draw-in']),
  'plan-a-draw-in-2xl': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-draw-in-2xl']),
  'plan-a-draw-in-large': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-draw-in-large']),
  'plan-a-full-row2': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-full-row2']),
  'plan-a-hover-paths': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['plan-a-hover-paths']),
  'dots-fine': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['dots-fine']),
  'dots-loose': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['dots-loose']),
  'dots-airy': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['dots-airy']),
  'dots-bold': buildEdgeFlushConfig(CHOOSE_PATH_DESIGN_CHROME['dots-bold']),
}

// ---------------------------------------------------------------------------
// Plan-box row treatments (the PLAN D…S cards under the diagram).
// null = the stock card markup, untouched.
// 'big-letter' = Joanna's mock card: small "PLAN" eyebrow, a huge serif
// letter, subtitle underneath. 'stock' layout keeps the stock markup and only
// swaps the chrome classes (used for the red recommended-plan border).

export type PlanCardSpec = {
  layout: 'stock' | 'big-letter'
  /** Chrome for the whole button; replaces the stock chrome classes. */
  buttonClassName: string
  /**
   * Chrome for the SELECTED (filled) state. Omitted = the stock inverted
   * chrome — which carries an mt-2 the spec chrome may not have, so tapping
   * would shift the button. Keep the geometry classes identical to
   * buttonClassName and only swap the fill/text/border colors.
   */
  invertedButtonClassName?: string
  /** The "PLAN" eyebrow (big-letter layout only). */
  prefixClassName?: string
  /** The plan letter (big-letter layout only). */
  letterClassName?: string
  /** The subtitle line (big-letter layout only). */
  subtitleClassName?: string
}

export const SECONDARY_PLAN_CARDS: Record<ChoosePathDesignId, PlanCardSpec | null> = {
  stock: null,
  'mono-mock': {
    layout: 'big-letter',
    buttonClassName: 'w-full rounded-[10px] border border-black bg-transparent hover:bg-black/5 cursor-pointer transition-colors flex flex-col items-center justify-center gap-1 py-4 px-1 min-h-[120px]',
    prefixClassName: 'font-mono text-[10px] tracking-[0.15em]',
    letterClassName: 'font-serif text-[44px] leading-none',
    subtitleClassName: 'font-mono text-[11px] leading-snug text-center',
  },
  'no-boxes': null,
  'small-questions': null,
  'red-plan-a': null,
  'serif-mock-layout': {
    layout: 'big-letter',
    buttonClassName: 'w-full rounded-md border-2 border-black/50 bg-transparent hover:bg-black/5 cursor-pointer transition-colors flex flex-col items-center justify-center gap-1 py-4 px-1 min-h-[120px] font-sans',
    prefixClassName: 'text-[11px] font-bold tracking-wider',
    letterClassName: 'font-serif text-[40px] leading-none',
    subtitleClassName: 'text-[11px] leading-snug text-center',
  },
  'lg-both-rails': null,
  'xl-both-rails': null,
  'lg-right-rail': null,
  'lg-bold-lead': null,
  'bigger-labels': null,
  'bold-lead-even': null,
  'bold-lead-2xl': null,
  'quiet-hairline': null,
  'quiet-dotted': null,
  'quiet-dashed': null,
  'quiet-gray': null,
  'quiet-no-heads': null,
  'even-left25': null,
  'dots-tight': null,
  'dots-tight-serif-labels': null,
  'dots-packed': null,
  'plan-a-bold-path': null,
  'plan-a-heads-all': null,
  'plan-a-no-heads': null,
  // Stock chrome minus the mt-2: the plan tops sit exactly where the
  // terminal lines end, matching the upper rows' 5px clearance.
  'plan-a-draw-in': {
    layout: 'stock',
    buttonClassName: 'w-full rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 bg-transparent border-black/50 hover:bg-black/5 cursor-pointer',
  },
  'plan-a-draw-in-2xl': {
    layout: 'stock',
    buttonClassName: 'w-full rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 bg-transparent border-black/50 hover:bg-black/5 cursor-pointer',
  },
  'plan-a-draw-in-large': {
    layout: 'stock',
    buttonClassName: 'w-full rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 bg-transparent border-black/50 hover:bg-black/5 cursor-pointer',
  },
  // Every card the SAME size: a shared min-height that fits a two-line
  // subtitle, and the subtitle unclamped (no stock max-w-[50px] band) so
  // wrapping is consistent across the four cards. Outline full black
  // (Joanna 2026-07-09) — was border-black/50.
  'plan-a-full-row2': {
    layout: 'stock',
    buttonClassName: 'w-full rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 bg-transparent border-black hover:bg-black/5 cursor-pointer min-[1000px]:min-h-[96px]',
    // Tapped/selected: same geometry, black fill — the button must not move
    // (Joanna 2026-07-09: "just keep them in the same position and fill
    // them in").
    invertedButtonClassName: 'w-full rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 bg-black text-white border-black cursor-pointer min-[1000px]:min-h-[96px]',
    subtitleClassName: 'font-sans hidden min-[1000px]:block text-center font-normal mt-2 text-[11px] leading-[15px]',
  },
  // Hovered plan's outline goes full black, matching its emphasized path.
  'plan-a-hover-paths': {
    layout: 'stock',
    buttonClassName: 'w-full rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 bg-transparent border-black/50 hover:border-black hover:bg-black/5 cursor-pointer',
  },
  // Stock card anatomy, outline at dark gray to match the gray-boxes bubbles.
  'gray-boxes': {
    layout: 'stock',
    buttonClassName: 'w-full mt-2 rounded-md font-sans text-base font-bold tracking-wide transition-colors border flex flex-col items-center justify-center py-4 border-2 bg-transparent border-black/70 hover:bg-black/5 cursor-pointer',
  },
  'dots-fine': null,
  'dots-loose': null,
  'dots-airy': null,
  'dots-bold': null,
}

export const PRIMARY_PLAN_CARDS: Record<ChoosePathDesignId, PlanCardSpec | null> = {
  stock: null,
  // The hero card of the mock: same anatomy as its siblings, one size up,
  // with the thick border doing the "recommended" marking.
  'mono-mock': {
    layout: 'big-letter',
    buttonClassName: 'w-full rounded-[10px] border-[3px] border-black bg-transparent group-hover:bg-black/5 cursor-pointer transition-colors flex flex-col items-center justify-center gap-1 py-5 px-1 min-h-[145px]',
    prefixClassName: 'font-mono text-[11px] tracking-[0.15em]',
    letterClassName: 'font-serif text-[58px] leading-none',
    subtitleClassName: 'font-mono text-[12px] leading-snug text-center',
  },
  'no-boxes': null,
  'small-questions': null,
  // Stock card anatomy; the border goes red — the only red in the figure.
  'red-plan-a': {
    layout: 'stock',
    buttonClassName: 'rounded-md font-bold tracking-wide transition-colors cursor-pointer border flex flex-col flex-1 items-center w-full justify-center py-4 px-3 sm:px-3 md:px-4 border-4 text-2xl text-nowrap min-h-[110px] border-[var(--race-background)] group-hover:bg-black/5',
  },
  'serif-mock-layout': {
    layout: 'big-letter',
    buttonClassName: 'w-full rounded-md border-4 border-black bg-transparent group-hover:bg-black/5 cursor-pointer transition-colors flex flex-col items-center justify-center gap-1 py-5 px-1 min-h-[145px] font-sans',
    prefixClassName: 'text-[12px] font-bold tracking-wider',
    letterClassName: 'font-serif text-[52px] leading-none',
    subtitleClassName: 'text-[12px] leading-snug text-center',
  },
  'lg-both-rails': null,
  'xl-both-rails': null,
  'lg-right-rail': null,
  'lg-bold-lead': null,
  'bigger-labels': null,
  'bold-lead-even': null,
  'bold-lead-2xl': null,
  'quiet-hairline': null,
  'quiet-dotted': null,
  'quiet-dashed': null,
  'quiet-gray': null,
  'quiet-no-heads': null,
  'even-left25': null,
  'dots-tight': null,
  'dots-tight-serif-labels': null,
  'dots-packed': null,
  'plan-a-bold-path': null,
  'plan-a-heads-all': null,
  'plan-a-no-heads': null,
  'plan-a-draw-in': null,
  'plan-a-draw-in-2xl': null,
  'plan-a-draw-in-large': null,
  'plan-a-full-row2': null,
  'plan-a-hover-paths': null,
  // Stock hero card, thick border at the same dark gray.
  'gray-boxes': {
    layout: 'stock',
    buttonClassName: 'rounded-md font-bold tracking-wide transition-colors cursor-pointer border flex flex-col flex-1 items-center w-full justify-center py-4 px-3 sm:px-3 md:px-4 border-4 text-2xl text-nowrap min-h-[110px] border-black/70 bg-transparent group-hover:bg-black/5',
  },
  'dots-fine': null,
  'dots-loose': null,
  'dots-airy': null,
  'dots-bold': null,
}
